import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Params } from '@angular/router';
import { IconsEnum } from '@uigy/generated/icons.enum';
import {
  FindingsPolicyViolationsComponent,
} from '@uigy/modules/findings/components/findings-policy-violations/findings-policy-violations.component';
import {
  PolicyFindingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings.service';
import {
  FindingsSidePanelComponent,
} from '@uigy/modules/findings/components/findings-side-panel/findings-side-panel.component';
import {
  PolicyFindingsSideDrawerService,
} from '@uigy/modules/findings/components/findings-side-panel/services/policy-findings-side-drawer.service';
import {
  VulnerabilityFindingsSideDrawerService,
} from '@uigy/modules/findings/components/findings-side-panel/services/vulnerability-findings-side-drawer.service';
import {
  FindingsVulnerabilitiesComponent,
} from '@uigy/modules/findings/components/findings-vulnerabilities/findings-vulnerabilities.component';
import {
  VulnerabilityFindingsService,
} from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings.service';
import { PluginsRoutingModule } from '@uigy/modules/plugins/plugins-routing.module';
import { UpdatesModule } from '@uigy/modules/settings/modules/updates/updates.module';
import { TabsComponent } from '@uigy/shared/components/tabs/tabs.component';
import { FindingsTabQueryParams, TabGroup } from '@uigy/shared/components/tabs/tabs.enums';
import { Tab, TabConfig } from '@uigy/shared/components/tabs/tabs.interface';
import { MaterialModule } from '@uigy/shared/material-module/material.module';
import {
  PluginsSystemMessagesComponent
} from '@uigy/shared/plugins-grid/components/plugins-notifications/plugins-system-messages.component';

import { HeadingComponent } from '@uigy/shared/components/heading/heading.component';
import { ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { HOST_ATTR_APP_ID } from '@uigy/shared/directives/app-id/host-attributes.constant';
import { LanguageService } from '@uigy/shared/services/language.service';
import { TabsUrlParametersService } from '@uigy/shared/services/url-params/tabs-url-parameters.service';
import { I18NextModule } from 'angular-i18next';
import { FindingsDrawerQueryParams } from '@uigy/shared/components/ui-drawer/drawer.enums';
import { SideDrawerUrlParametersService } from '@uigy/shared/services/url-params/side-drawer-url-parameters.service';


@Component({
  selector: 'app-findings-page',
  standalone: true,
  providers: [VulnerabilityFindingsSideDrawerService],
  imports: [
    PluginsRoutingModule,
    HeadingComponent,
    MaterialModule,
    I18NextModule,
    UpdatesModule,
    FindingsVulnerabilitiesComponent,
    FindingsPolicyViolationsComponent,
    FindingsSidePanelComponent,
    TabsComponent,
    PluginsSystemMessagesComponent,
  ],
  templateUrl: './findings-page.component.html',
  styleUrl: './findings-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindingsPageComponent implements OnInit {
  public vulnerabilityFindingsSideDrawerService = inject(VulnerabilityFindingsSideDrawerService);
  public policyFindingsSideDrawerService = inject(PolicyFindingsSideDrawerService);
  private languageService = inject(LanguageService);
  private tabsUrlParametersService = inject(TabsUrlParametersService);
  private sideDrawerUrlParametersService = inject(SideDrawerUrlParametersService);

  public readonly findingsTexts = this.languageService.texts.findings;

  public tabs: Tab[] = [];
  @ViewChild('vulnerabilities', { static: true }) vulnerabilities?: TemplateRef<FindingsVulnerabilitiesComponent>;
  @ViewChild('policyViolations', { static: true }) policyViolations?: TemplateRef<FindingsPolicyViolationsComponent>;

  public readonly tabConfig: TabConfig = {
    tabId: TabGroup.FindingsTab,
    clearQueryOnTabChange: true,
  };

  public static readonly defaultNavigation: Params = {
    [TabGroup.FindingsTab]: FindingsTabQueryParams.Vulnerabilities,
    ...VulnerabilityFindingsService.baseURLParams,
  };

  @HostBinding(HOST_ATTR_APP_ID)
  public readonly hostAppId: string = AppIdsEnum.page_findings;

  public ngOnInit(): void {
    if (this.vulnerabilities && this.policyViolations) {
      this.tabs = [
        {
          title: this.findingsTexts.vulnerability_findings.tab_title,
          icon: IconsEnum.vulnerability,
          content: this.vulnerabilities,
          queryParams: FindingsTabQueryParams.Vulnerabilities,
          additionalQueryParams: {
            ...VulnerabilityFindingsService.baseURLParams,
          },
          appId: AppIdsEnum.findings_vulnerability,
        },
        {
          title: this.findingsTexts.policy_violations.tab_title,
          icon: IconsEnum.bell,
          content: this.policyViolations,
          queryParams: FindingsTabQueryParams.PolicyViolations,
          additionalQueryParams: {
            ...PolicyFindingsService.baseURLParams,
          },
          appId: AppIdsEnum.findings_policy,
        },
      ];

    }

    // Register tab change callback with the service to clear drawers
    this.tabsUrlParametersService.registerTabChangeCallback({
      tabId: TabGroup.FindingsTab,
      callback: (oldValue: string | undefined, newValue: string | undefined) => {
        if (oldValue && oldValue !== newValue) {
          // Read URL directly to avoid stale cache during browser back navigation
          const urlParams = new URLSearchParams(window.location.search);
          const selectedFindingId = urlParams.get(FindingsDrawerQueryParams.SelectedFindingId);
          const selectedPolicyFindingId = urlParams.get(FindingsDrawerQueryParams.SelectedPolicyFindingId);

          // Check if we're restoring state via browser back (current tab has its drawer parameter in URL)
          const isRestoringState = (newValue === FindingsTabQueryParams.Vulnerabilities && selectedFindingId) ||
                                   (newValue === FindingsTabQueryParams.PolicyViolations && selectedPolicyFindingId);

          if (!isRestoringState) {
            // Only clear drawers and parameters when actively switching tabs (not restoring via back button)
            const paramsToClear: FindingsDrawerQueryParams[] = [];

            if (newValue === FindingsTabQueryParams.Vulnerabilities) {
              // Switching TO vulnerabilities tab - close policy drawer and clear its parameter
              if (this.policyFindingsSideDrawerService.isDrawerOpen()) {
                this.policyFindingsSideDrawerService.closeDrawer({ skipUrlUpdate: true });
              }
              if (selectedPolicyFindingId) {
                paramsToClear.push(FindingsDrawerQueryParams.SelectedPolicyFindingId);
              }
            } else if (newValue === FindingsTabQueryParams.PolicyViolations) {
              // Switching TO policy tab - close vulnerabilities drawer and clear its parameter
              if (this.vulnerabilityFindingsSideDrawerService.isDrawerOpen()) {
                this.vulnerabilityFindingsSideDrawerService.closeDrawer({ skipUrlUpdate: true });
              }
              if (selectedFindingId) {
                paramsToClear.push(FindingsDrawerQueryParams.SelectedFindingId);
              }
            }

            // Clear parameters that don't belong to the new tab
            if (paramsToClear.length > 0) {
              this.sideDrawerUrlParametersService.removeSideDrawerParams(paramsToClear, { historyMode: 'replace' });
            }
          }
          // If restoring state (browser back), don't interfere - let drawer sync handle it
        }
      }
    });
  }

  protected readonly ActionButtonVariant = ActionButtonVariant;
  protected readonly TabQueryParams = FindingsTabQueryParams;
}
